import { Component, OnInit, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ModalController, IonContent, InfiniteScrollCustomEvent, IonInfiniteScroll } from '@ionic/angular';
import { WeighindetailsPage } from './weighindetails/weighindetails.page';
import { LocaldataService } from '../../service/localdata/localdata.service';
import { Router } from '@angular/router';

interface GroupedWeighins {
  date: string;
  items: any[];
}

@Component({
  selector: 'app-driverweighins',
  templateUrl: './driverweighins.page.html',
  styleUrls: ['./driverweighins.page.scss'],
})
export class DriverweighinsPage implements OnInit {
  @ViewChild(IonContent) content!: IonContent;
  @ViewChild(IonInfiniteScroll) infiniteScroll!: IonInfiniteScroll;
  
  searchTerm: string = '';
  groupedWeighins: GroupedWeighins[] = [];
  
  private page: number = 1;
  private pageSize: number = 20;
  private isLastPage: boolean = false;
  isLoading: boolean = false;
  private searchDebouncer: any;

  noResults: boolean = false;
  searchActive: boolean = false;

  constructor(
    private datePipe: DatePipe, 
    private modelCtrl: ModalController, 
    private localData: LocaldataService,
    private router: Router
  ) { }

  ngOnInit() {
    //console.log('INIT fetch');
    //this.fetchWeighins();
  }


  ionViewWillEnter(): void {
    console.log('WILL INIT fetch');
    this.fetchWeighins();
  }

  async doRefresh(event: any) {
    this.page = 1;
    this.isLastPage = false;
    this.groupedWeighins = [];
    
    if (this.infiniteScroll) {
      this.infiniteScroll.disabled = false;
    }
    
    await this.fetchWeighins(event);
  }

  loadMore(event: InfiniteScrollCustomEvent) {
    console.log('LoadMore called:', { 
      currentPage: this.page, 
      isLastPage: this.isLastPage,
      searchTerm: this.searchTerm,
      searchActive: this.searchActive,
      groupedWeighinsLength: this.groupedWeighins.length
    });
  
    if (this.isLastPage) {
      console.log('Last page reached, completing infinite scroll');
      event.target.complete();
      return;
    }
      
    this.page++;
    this.fetchWeighins(undefined, event);
  }

fetchWeighins(refreshEvent?: any, infiniteScrollEvent?: InfiniteScrollCustomEvent, isReset: boolean = false) {
  if (this.isLoading) {
    console.log('Already loading, skipping fetch');
    return;
  }
  this.isLoading = true;

  const skip = (this.page - 1) * this.pageSize;
  
  console.log('Fetching weighins:', { 
    page: this.page, 
    skip, 
    searchActive: this.searchActive,
    searchTerm: this.searchTerm,
    currentGroupsLength: this.groupedWeighins.length,
    isReset
  });

  // Always pass the searchTerm if searchActive is true
  const activeSearchTerm = this.searchActive ? this.searchTerm : undefined;

  this.localData.getWeighinsByDriver(skip, this.pageSize, activeSearchTerm).subscribe(
    response => {
      console.log('Response received:', {
        dataLength: response.data.length,
        isLastPage: response.data.length < this.pageSize,
        totalGroups: this.groupedWeighins.length
      });

      if (this.page === 1 || isReset) {
        this.groupWeighins(response.data);
      } else {
        this.appendToGroups(response.data);
      }
      
      this.isLastPage = response.data.length < this.pageSize;
      this.isLoading = false;
      
      if (refreshEvent) {
        refreshEvent.target.complete();
      }
      
      if (infiniteScrollEvent) {
        infiniteScrollEvent.target.complete();
      }

      // Only disable infinite scroll if we're actually at the last page
      if (this.infiniteScroll && this.isLastPage) {
        console.log('Disabling infinite scroll - last page reached');
        this.infiniteScroll.disabled = true;
      } else if (this.infiniteScroll) {
        console.log('Ensuring infinite scroll is enabled');
        this.infiniteScroll.disabled = false;
      }
    },
    error => {
      console.error('Error fetching weighins:', error);
      this.isLoading = false;
      if (refreshEvent) refreshEvent.target.complete();
      if (infiniteScrollEvent) infiniteScrollEvent.target.complete();
    }
  );
}

  private groupWeighins(weighins: any[]) {
    const groups = this.createGroups(weighins);
    this.groupedWeighins = groups;
  }

  private createGroups(weighins: any[]): GroupedWeighins[] {
    weighins.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    const groups = weighins.reduce((groups: { [key: string]: any[] }, weighin) => {
      const date = this.datePipe.transform(weighin.timestamp, 'yyyy-MM-dd');
      if (!date) return groups;
      
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(weighin);
      return groups;
    }, {});

    return Object.keys(groups).map(date => ({
      date: this.getFormattedDateHeader(date),
      items: groups[date]
    }));
  }

  private appendToGroups(newWeighins: any[]) {
    const newGrouped = this.createGroups(newWeighins);
    
    newGrouped.forEach(newGroup => {
      const existingGroup = this.groupedWeighins.find(g => g.date === newGroup.date);
      if (existingGroup) {
        existingGroup.items.push(...newGroup.items);
        existingGroup.items.sort((a, b) => 
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );
      } else {
        this.groupedWeighins.push(newGroup);
      }
    });

    this.groupedWeighins.sort((a, b) => {
      const dateA = this.parseDateFromHeader(a.date);
      const dateB = this.parseDateFromHeader(b.date);
      return dateB.getTime() - dateA.getTime();
    });
  }

  private parseDateFromHeader(header: string): Date {
    // Extract the date part from "Weekday den DD.MM.YYYY"
    const datePart = header.split(' den ')[1];
    const [day, month, year] = datePart.split('.');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }

  // Rest of your existing methods remain the same
  getFormattedDateHeader(dateStr: string): string {
    const date = new Date(dateStr);
    const weekdays = ['Søndag', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag'];
    const weekday = weekdays[date.getDay()];
    const formattedDate = this.datePipe.transform(date, 'dd.MM.yyyy');
    
    return `${weekday} den ${formattedDate}`;
  }

  getFormattedDate(dateStr: string): string {
    let date = this.datePipe.transform(dateStr, 'dd-MM-yy');
    let time = this.datePipe.transform(dateStr, 'HH.mm');
    return `${date} Kl. ${time}`;
  }

  async showWeighin(weighin: any) {
    const modal = await this.modelCtrl.create({
      component: WeighindetailsPage,
      componentProps: { 
        weighin: weighin,
        modalCtrl: this.modelCtrl
      }
    });
    await modal.present();
  }


  /*showWeighin(weighinObj: any) {
    const serialnumber =  weighinObj.serialnumber;
    const weighinId = weighinObj.id;
    // First, mark the weigh-in as read in the database
    
    this.router.navigate([`vejeboner/vejebon/${weighinId}`], { queryParams: { serialnumber: serialnumber } });
  }*/





  searchEntries() {
    if (this.searchDebouncer) {
        clearTimeout(this.searchDebouncer);
    }

    // If searchTerm is empty and search was previously active, clear the search
    if (!this.searchTerm && this.searchActive) {
        this.clearSearch();
        return;
    }

    this.searchActive = true;

    this.searchDebouncer = setTimeout(() => {
        this.page = 1;
        this.isLastPage = false;
        this.groupedWeighins = [];
        
        if (this.infiniteScroll) {
            this.infiniteScroll.disabled = false;
        }

        const skip = (this.page - 1) * this.pageSize;
        this.isLoading = true;

        this.localData.getWeighinsByDriver(skip, this.pageSize, this.searchTerm).subscribe(
            response => {
                this.groupWeighins(response.data);
                this.isLastPage = response.data.length < this.pageSize;
                this.isLoading = false;
                this.noResults = this.groupedWeighins.length === 0;
                
                this.content.scrollToTop(300);
            },
            error => {
                console.error('Error searching weighins:', error);
                this.isLoading = false;
                this.noResults = true;
            }
        );
    }, 300);
}

  clearSearch() {
    console.log('Clear search triggered');
    this.searchTerm = '';
    this.searchActive = false;
    this.noResults = false;
    this.page = 1;
    this.isLastPage = false;
    this.groupedWeighins = [];
      
    if (this.infiniteScroll) {
      console.log('Enabling infinite scroll');
      this.infiniteScroll.disabled = false;
    }
  
    // Force complete any pending infinite scroll
    this.infiniteScroll?.complete();
      
    this.fetchWeighins(undefined, undefined, true);  // Added new parameter
    this.content.scrollToTop(300);
  }

  getDisplayName(weighin: any): string {
    return weighin.farmer?.displayname || 'Unknown';
  }
}